import classNames from 'classnames';
import useEmblaCarousel, {
	type EmblaCarouselType,
} from 'embla-carousel-react';
import React from 'react';

import getArrayItemAtSafeIndex from '~/utilities/getArrayItemAtSafeIndex';

import {
	notEmpty,
} from '~/utilities/typeCheck';



function useReinitialize(
	emblaApi: EmblaCarouselType | undefined,
	slides: Array<React.ReactNode>,
): void {
	const [, setForceUpdate] = React.useState(0);

	React.useLayoutEffect(
		() => {
			if (emblaApi && emblaApi.slideNodes().length !== slides.length) {
				emblaApi.reInit();
				setForceUpdate((forceUpdate) => forceUpdate + 1);
			}
		},
		[
			emblaApi,
			slides,
		],
	);
}



function useActiveSlide(
	emblaApi: EmblaCarouselType | undefined,
	activeSlideIndex?: number,
	swipe?: boolean,
): void {
	React.useLayoutEffect(
		() => {
			if (emblaApi && activeSlideIndex !== undefined) {
				emblaApi.scrollTo(activeSlideIndex, !swipe);
			}
		},
		[
			activeSlideIndex,
			emblaApi,
			swipe,
		],
	);
}



function useAdaptiveHeight(
	emblaApi: EmblaCarouselType | undefined,
	adaptiveHeight?: boolean,
	children?: React.ReactNode,
): React.CSSProperties['height'] {
	const [containerHeight, setContainerHeight] = React.useState<React.CSSProperties['height']>('auto');

	React.useLayoutEffect(
		() => {
			if (!emblaApi || !adaptiveHeight) {
				return;
			}

			const updateHeight = () => {
				const childNodes = emblaApi.slideNodes();
				const activeNode = getArrayItemAtSafeIndex(childNodes, emblaApi.selectedScrollSnap());

				if (activeNode.firstChild instanceof HTMLElement) {
					setContainerHeight(activeNode.firstChild.clientHeight);
				} else {
					setContainerHeight('auto');
				}
			};

			updateHeight();

			emblaApi.on('reInit', updateHeight);
			emblaApi.on('select', updateHeight);

			return () => {
				emblaApi.off('reInit', updateHeight);
				emblaApi.off('select', updateHeight);
			};
		},
		[
			adaptiveHeight,
			children,
			emblaApi,
		],
	);

	return containerHeight;
}



function useSlideChangeListener(
	emblaApi: EmblaCarouselType | undefined,
	callback?: (index: number) => void,
): void {
	React.useEffect(
		() => {
			if (!emblaApi || !callback) {
				return;
			}

			const listener = () => {
				const index = emblaApi.selectedScrollSnap();

				callback(index);
			};

			emblaApi.on('select', listener);

			return () => {
				emblaApi.off('select', listener);
			};
		},
		[
			callback,
			emblaApi,
		],
	);
}



function usePointerListener(
	emblaApi: EmblaCarouselType | undefined,
	onPointerDownCallback?: (index: number) => void,
	onPointerUpCallback?: (index: number) => void,
): void {
	React.useEffect(
		() => {
			if (!emblaApi) {
				return;
			}

			const pointerDownListener = () => {
				const index = emblaApi.selectedScrollSnap();

				if (onPointerDownCallback) {
					onPointerDownCallback(index);
				}
			};

			const pointerUpListener = () => {
				const index = emblaApi.selectedScrollSnap();

				if (onPointerUpCallback) {
					onPointerUpCallback(index);
				}
			};

			emblaApi.on('pointerDown', pointerDownListener);
			emblaApi.on('pointerUp', pointerUpListener);

			return () => {
				emblaApi.off('pointerDown', pointerDownListener);
				emblaApi.off('pointerUp', pointerUpListener);
			};
		},
		[
			emblaApi,
			onPointerDownCallback,
			onPointerUpCallback,
		],
	);
}



type Props = {
	activeSlideIndex?: number,
	adaptiveHeight?: boolean,
	animatedHeightChange?: boolean,
	children?: React.ReactNode,
	draggable?: boolean,
	infinite?: boolean,
	onPointerDownCallback?: (index: number) => void,
	onPointerUpCallback?: (index: number) => void,
	onSlideChangeCallback?: (index: number) => void,
	selectableText?: boolean,
	slideGaps?: number,
	swipe?: boolean,
};



const CarouselBlocks: React.FC<Props> = (props) => {
	const {
		activeSlideIndex,
		adaptiveHeight = true,
		animatedHeightChange = false,
		children,
		draggable = true,
		infinite = false,
		onPointerDownCallback,
		onPointerUpCallback,
		onSlideChangeCallback,
		selectableText = false,
		slideGaps = 0,
		swipe = true,
	} = props;

	const [startIndex] = React.useState(activeSlideIndex ?? 0);

	const [emblaRef, emblaApi] = useEmblaCarousel({
		draggable,
		loop: infinite,
		skipSnaps: false,
		speed: 20,
		startIndex,
	});

	const slides = React.Children.toArray(children)
		.filter(notEmpty);

	const containerHeight = useAdaptiveHeight(emblaApi, adaptiveHeight);

	useReinitialize(emblaApi, slides);
	useActiveSlide(emblaApi, activeSlideIndex, swipe);

	useSlideChangeListener(emblaApi, onSlideChangeCallback);
	usePointerListener(emblaApi, onPointerDownCallback, onPointerUpCallback);

	const componentClasses = classNames({
		'carousel-blocks': true,
		'carousel-blocks--animated-height-change': animatedHeightChange,
		'carousel-blocks--selectable-text': selectableText,
	});

	return (
		<div
			className={componentClasses}
			ref={emblaRef}
			style={{
				height: animatedHeightChange ? containerHeight : 'auto',
			}}
		>
			<div
				className="carousel-blocks__container"
				style={{
					transform: `translate3d(${startIndex * -100}%, 0px, 0px)`,
					marginLeft: `-${slideGaps}px`,
				}}
			>
				{slides.map((slide, index) => {
					let height: React.CSSProperties['height'] | undefined;
					let overflow: React.CSSProperties['overflow'] | undefined;

					if (adaptiveHeight) {
						const activeSlide = emblaApi?.selectedScrollSnap();

						if (index !== activeSlide) {
							height = 10;
							overflow = 'hidden';
						}
					}

					return (
						<div
							className="carousel-blocks__slide"
							key={`slide-${index}`}
							style={{
								paddingLeft: `${slideGaps}px`,
								height,
								overflow,
							}}
						>
							{slide}
						</div>
					);
				})}
			</div>
		</div>
	);
};



export default CarouselBlocks;
